import { useCallback, useEffect, useRef } from 'react'

interface UsePollingWithBackoffOptions {
  fetchFn: () => Promise<boolean>
  shouldStop?: boolean
  initialIntervalMs?: number
  maxIntervalMs?: number
  backoffFactor?: number
}

/**
 * Polls an async function with exponential backoff.
 * Pauses when the browser tab is hidden (Page Visibility API) and
 * resumes with a reset interval when the tab becomes visible again.
 */
export const usePollingWithBackoff = ({
  fetchFn,
  shouldStop = false,
  initialIntervalMs = 1000,
  maxIntervalMs = 30000,
  backoffFactor = 2,
}: UsePollingWithBackoffOptions) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const currentIntervalRef = useRef(initialIntervalMs)
  const shouldStopRef = useRef(shouldStop)
  const fetchFnRef = useRef(fetchFn)

  // Keep refs in sync
  shouldStopRef.current = shouldStop
  fetchFnRef.current = fetchFn

  const clearPendingTimeout = useCallback(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  const runFetch = useCallback(async () => {
    if (shouldStopRef.current) return true

    try {
      return await fetchFnRef.current()
    } catch {
      // Swallow — next poll will retry
      return false
    }
  }, [])

  const scheduleNext = useCallback(() => {
    if (shouldStopRef.current) return

    timeoutRef.current = setTimeout(async () => {
      if (shouldStopRef.current) return

      const shouldStopAfterFetch = await runFetch()
      if (shouldStopAfterFetch || shouldStopRef.current) return

      // Increase interval with backoff
      currentIntervalRef.current = Math.min(
        currentIntervalRef.current * backoffFactor,
        maxIntervalMs
      )

      scheduleNext()
    }, currentIntervalRef.current)
  }, [backoffFactor, maxIntervalMs, runFetch])

  const refetch = useCallback(async () => {
    if (shouldStopRef.current) return

    currentIntervalRef.current = initialIntervalMs
    clearPendingTimeout()

    const shouldStopAfterFetch = await runFetch()
    if (!shouldStopAfterFetch && !shouldStopRef.current) {
      scheduleNext()
    }
  }, [clearPendingTimeout, initialIntervalMs, runFetch, scheduleNext])

  useEffect(() => {
    // Initial fetch immediately
    const init = async () => {
      if (shouldStopRef.current) return

      const shouldStopAfterFetch = await runFetch()
      if (!shouldStopAfterFetch && !shouldStopRef.current) {
        scheduleNext()
      }
    }

    init()

    // Page Visibility API handler
    const handleVisibilityChange = async () => {
      if (document.hidden) {
        clearPendingTimeout()
      } else {
        if (shouldStopRef.current) return

        // Reset backoff and fetch immediately on tab focus
        currentIntervalRef.current = initialIntervalMs
        clearPendingTimeout()

        const shouldStopAfterFetch = await runFetch()
        if (!shouldStopAfterFetch && !shouldStopRef.current) {
          scheduleNext()
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      clearPendingTimeout()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [clearPendingTimeout, initialIntervalMs, runFetch, scheduleNext])

  // Stop polling when shouldStop becomes true
  useEffect(() => {
    if (shouldStop) {
      clearPendingTimeout()
    }
  }, [shouldStop, clearPendingTimeout])

  return { refetch }
}
